import { useState } from 'react';
import { Alert, Badge, Button, Input, Spinner, Table } from 'reactstrap';
import { useTenantContext } from '../../auth/useTenantContext';
import {
  useAddExcludedLocation, useExcludedLocations, useRemoveExcludedLocation,
} from '../../api/adminWarehouses';

export interface WarehouseExcludedLocationsProps {
  warehouseId: number;
  /** Amministratore di azienda. Gli altri vedono in sola lettura. */
  canEdit: boolean;
}

/**
 * Le ubicazioni da **non prelevare** di un magazzino.
 *
 * I titoli che si trovano qui non vengono comunicati all'operatore: non
 * diventano nemmeno lavoro, quindi lui non si accorge che erano in lista.
 * Restano però visibili al capo magazzino e, verso Book, **valgono come
 * prelevati per intero**.
 *
 * Due cose che l'interfaccia deve dire, perché nessuna delle due è intuibile:
 *
 *  1. **la corrispondenza è esatta** — `0001` non cattura `0001A`, e gli zeri
 *     iniziali contano;
 *  2. **togliere un'ubicazione non agisce all'indietro** — le liste già
 *     contrassegnate restano tali. Significa "da adesso non escludo più".
 */
export function WarehouseExcludedLocations({ warehouseId, canEdit }: WarehouseExcludedLocationsProps) {
  const { token, tenantCode } = useTenantContext();
  const { data, isLoading } = useExcludedLocations(token, tenantCode, warehouseId);
  const add = useAddExcludedLocation(token, tenantCode, warehouseId);
  const del = useRemoveExcludedLocation(token, tenantCode, warehouseId);

  const [nuovo, setNuovo] = useState('');
  const [errore, setErrore] = useState<string | null>(null);
  const [esito, setEsito] = useState<string | null>(null);

  const aggiungi = async () => {
    setErrore(null);
    setEsito(null);
    if (nuovo.trim() === '') {
      setErrore('Indica un codice ubicazione.');
      return;
    }
    try {
      const res = await add.mutateAsync(nuovo) as { rows_flagged?: number } | undefined;
      setNuovo('');
      // Il numero di righe toccate e' l'unico riscontro che l'inserimento ha
      // avuto effetto: senza, si salva e non si vede succedere niente.
      const n = res?.rows_flagged ?? 0;
      setEsito(n > 0
        ? `Ubicazione aggiunta. ${n} ${n === 1 ? 'riga contrassegnata' : 'righe contrassegnate'} sulle liste non ancora divise.`
        : 'Ubicazione aggiunta. Nessuna riga da contrassegnare sulle liste attuali.');
    } catch (e: unknown) {
      // `authedFetch` mette il corpo della risposta sotto `.body`, non alla
      // radice dell'errore: leggerlo dal posto sbagliato farebbe cadere ogni
      // messaggio sul generico "non e' stato possibile".
      const err = e as { body?: { code?: string; detail?: string } };
      const code = err?.body?.code;
      setErrore(code === 'location_code_duplicate'
        ? 'Questa ubicazione è già esclusa per questo magazzino.'
        : code === 'location_code_blank'
          ? 'Il codice ubicazione non può essere vuoto.'
          : err?.body?.detail ?? 'Non è stato possibile aggiungere l\'ubicazione.');
    }
  };

  const rimuovi = async (id: number, codice: string) => {
    setErrore(null);
    setEsito(null);
    try {
      await del.mutateAsync(id);
      setEsito(`${codice} non verrà più esclusa dalle prossime liste. Le righe già contrassegnate restano tali.`);
    } catch {
      setErrore('Non è stato possibile rimuovere l\'ubicazione.');
    }
  };

  if (isLoading) return <div className="p-3"><Spinner size="sm" /> Caricamento…</div>;

  const items = data ?? [];

  return (
    <div className="d-flex flex-column gap-3">
      <p className="text-muted small mb-0">
        I titoli che si trovano in queste ubicazioni <strong>non vengono comunicati
        all'operatore</strong>. Restano visibili sulla lista e, verso Book, valgono
        come prelevati per intero.
      </p>

      {canEdit && (
        <div className="d-flex align-items-start gap-2 flex-wrap">
          <div className="d-flex flex-column">
            <Input
              style={{ width: 220 }}
              aria-label="Codice ubicazione"
              placeholder="es. 0001"
              value={nuovo}
              onChange={(e) => setNuovo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void aggiungi(); }}
            />
            <span className="form-text text-muted">
              Corrispondenza esatta: <code>0001</code> non esclude <code>0001A</code>,
              e gli zeri iniziali contano.
            </span>
          </div>
          <Button color="primary" disabled={add.isPending} onClick={() => void aggiungi()}>
            <i className="bi bi-plus-lg me-1" />Aggiungi
          </Button>
        </div>
      )}

      {errore && <Alert color="danger" className="mb-0 py-2" role="alert">{errore}</Alert>}
      {esito && <Alert color="success" className="mb-0 py-2">{esito}</Alert>}

      {items.length === 0 ? (
        <span className="text-muted small">
          Nessuna ubicazione esclusa: tutti i titoli vengono comunicati all'operatore.
        </span>
      ) : (
        <Table size="sm" className="mb-0 align-middle">
          <thead>
            <tr><th>Ubicazione</th><th style={{ width: 160 }}>Aggiunta il</th>{canEdit && <th style={{ width: 60 }} />}</tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td><Badge color="secondary" className="bg-secondary-subtle text-secondary">{r.location_code}</Badge></td>
                <td className="text-muted small">{new Date(r.created_at).toLocaleDateString('it-IT')}</td>
                {canEdit && (
                  <td className="text-end">
                    <Button color="link" size="sm" className="p-0 text-danger"
                            aria-label={`Rimuovi ${r.location_code}`}
                            onClick={() => void rimuovi(r.id, r.location_code)}>
                      <i className="bi bi-trash" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {canEdit && items.length > 0 && (
        <span className="text-muted small">
          <i className="bi bi-info-circle me-1" />
          Rimuovere un'ubicazione vale <strong>da adesso in poi</strong>: le righe già
          contrassegnate restano escluse.
        </span>
      )}
    </div>
  );
}
